// @vendors
import { useState } from "react";
import {
  ArrowUpTrayIcon,
  XMarkIcon,
  HeartIcon,
  BookmarkIcon,
  ShareIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/solid";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

// @types
import { CreatePostRequest } from "../../../../types/post.types";

// @store
import usePostStore from "../../../../store/usePostStore";
import useProfileStore from "../../../../store/useProfileStore";
import { useSpinnerStore } from "../../../../store/useSpinnerStore";
import { useCompanyStore } from "../../../../store/useCompanyStore";

// @components
import Modal from "../../../../components/Modals/Modal";

// @assets
import emoji from "../../../../assets/icons/emoji _message.svg";

// @config
import routesConfig from "../../../../config/routes.config";

// @services
import postService from "../../../../services/post.service"

export default function CreatePostPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openSpinner } = useSpinnerStore();
  const { data } = useCompanyStore();
  const companyData = data;
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [hashtags, setHashtags] = useState<string>("");
  //const [allowComments, setAllowComments] = useState(false);
  const [allowSound, setAllowSound] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const { professional } = useProfileStore();
  const [openModal, setOpenModal] = useState(false);
  const [imageToUpload, setImageToUpload] = useState<any>();

  const companyImage =
    professional?.companyImage ||
    `https://eu.ui-avatars.com/api/?name=${professional?.name || "User"}+${professional?.lastNames || ""
    }&size=250`;

  const createPost = usePostStore((state) => state.createPost);

  const handleHashtagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHashtags(e.target.value);
  };

  // In your submit handler:
  const handleSubmit = async () => {
    const postData: CreatePostRequest = {
      title,
      description,
      categoryId: companyData?.category?.categoryId ?? '',
      hashtags: hashtags.split(" "), // Convert space-separated hashtags to array
      includeAudio: allowSound,
    };
    const isValid = Object.values(postData).some((value) => value === "")
    if (isValid || !selectedImage) return toast.error(t("professional.reels.completeForm"));
    return submitData(postData);
  };

  const submitData = async (postData: CreatePostRequest) => {
    openSpinner(true);
    try {
      const response = await createPost(postData);
      submitImage(response?.postId);
    } catch (error) {
      openSpinner(false);
      toast.error(t("professional.reels.errorPost"));
    }
  }

  const submitImage = async (postId: string) => {
    try {
      await postService.addImagePost(imageToUpload, postId);
      openSpinner(false);
      setOpenModal(true)
    } catch (error) {
      openSpinner(false);
      toast.error(t("professional.reels.errorPostImage"));
    }
  }

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('reel', file);
      setImageToUpload(formData);
      const imageUrl = URL.createObjectURL(file);
      setSelectedImage(imageUrl);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const closeModal = () => {
    setTitle('');
    setDescription('');
    //setAllowComments(false);
    setAllowSound(false);
    setSelectedImage(null);
    setOpenModal(false);
    navigate(routesConfig.marketplaceProfessional);
  }

  return (
    <div className="flex w-full p-4 gap-[20px] lg:flex-row sm:flex-col h-[85vh] overflow-auto">
      {/* Gallery Selection */}
      <div className="lg:w-1/3 md:w-full p-5 bg-white dark:bg-dark-cBackground rounded-[8px] min-h-[600px]">
        <h3 className="text-[16px] font-semibold text-cText dark:text-dark-cText mb-4 text-center">
          {t("professional.reels.selectImage")}
        </h3>
        {!selectedImage ? (
          <div className="grid grid-cols-1 gap-2 mt-10">
            <label
              className="relative w-full h-24 bg-gray-300 dark:bg-dark-cBackground dark:border border-gray-700 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:bg-gray-400 transition-colors"
            >
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleImageUpload}
              />
              <ArrowUpTrayIcon className="h-6 w-6 text-white mb-1" />
              <span className="text-white font-medium">{companyData?.category?.name ?? ''}</span>
            </label>
          </div>
        ) : (
          <div className="relative">
            <img
              src={selectedImage}
              alt="Selected"
              className="w-full rounded-lg aspect-square object-cover"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 p-1 bg-red-500 rounded-full text-white hover:bg-red-600"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>

      {/* Form Section */}
      <div className="lg:w-1/3 md:w-full p-5 space-y-4 bg-white dark:bg-dark-cBackground rounded-[8px] flex flex-col gap-2 overflow-y-auto min-h-[600px]">
        <h3 className="text-[16px] font-semibold text-cText dark:text-dark-cText mb-5 text-center">
          {t("professional.reels.newPublication")}
        </h3>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-cText dark:text-dark-cText">
            {t("professional.reels.labelTitle")}
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("professional.reels.placeholderTitle")}
            className="w-full p-3 border border-cBorder dark:border-dark-cBorder rounded-lg
                 bg-white dark:bg-dark-cBackgroundSecondary
                 text-cText dark:text-dark-cText
                 placeholder-cTextSecondary dark:placeholder-dark-cTextSecondary
                 focus:outline-none focus:ring-2 focus:ring-bgBrand dark:focus:ring-dark-bgBrand"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-cText dark:text-dark-cText">
            {t("professional.reels.description")}
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("professional.reels.placeholderDescription")}
            rows={4}
            className="w-full p-3 border border-cBorder dark:border-dark-cBorder rounded-lg
                 bg-white dark:bg-dark-cBackgroundSecondary
                 text-cText dark:text-dark-cText
                 placeholder-cTextSecondary dark:placeholder-dark-cTextSecondary
                 focus:outline-none focus:ring-2 focus:ring-bgBrand dark:focus:ring-dark-bgBrand"
          />
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-cText dark:text-dark-cText">
            {t("professional.reels.hashtags")}
          </label>
          <input
            type="text"
            value={hashtags}
            onChange={handleHashtagChange}
            placeholder={t("professional.reels.placeholderHashtag")}
            className="w-full p-3 border border-cBorder dark:border-dark-cBorder rounded-lg
                 bg-white dark:bg-dark-cBackgroundSecondary
                 text-cText dark:text-dark-cText
                 placeholder-cTextSecondary dark:placeholder-dark-cTextSecondary
                 focus:outline-none focus:ring-2 focus:ring-bgBrand dark:focus:ring-dark-bgBrand"
          />
        </div>

        {/*  <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-cText dark:text-dark-cText">
            {t("professional.reels.comments")}
          </label>
          <Switch
            checked={allowComments}
            onChange={setAllowComments}
            className={`${allowComments
              ? "bg-bgBrand dark:bg-dark-bgBrand"
              : "bg-bgDisabled dark:bg-dark-bgDisabled"
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
          >
            <span
              className={`${allowComments ? "translate-x-6" : "translate-x-1"
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div>

        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-cText dark:text-dark-cText">
            {t("professional.reels.audio")}
          </label>
          <Switch
            checked={allowSound}
            onChange={setAllowSound}
            className={`${allowSound
              ? "bg-bgBrand dark:bg-dark-bgBrand"
              : "bg-bgDisabled dark:bg-dark-bgDisabled"
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none`}
          >
            <span
              className={`${allowSound ? "translate-x-6" : "translate-x-1"
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
            />
          </Switch>
        </div> */}
      </div>

      {/* Mobile Preview */}
      <div className="lg:w-1/3 md:w-full p-4">
        <h3 className="text-[16px] font-semibold text-cText dark:text-dark-cText mb-2 text-center">
          {t("professional.reels.preview")}
        </h3>
        <div className="w-[320px] h-[600px] bg-black rounded-[40px] border-[14px] border-black dark:border-gray-700 shadow-lg mx-auto">
          <div className="relative w-full h-full bg-white dark:bg-dark-cBackground rounded-[26px] overflow-hidden">
            {/* Top Navigation */}
            <div className="absolute top-0 left-0 right-0 z-10">
              <div className={`${selectedImage ? 'flex flex-col w-full' : 'hidden'}`}>
                <div className="flex items-center px-4 pt-2">
                  <button className="p-2">
                    <ArrowLeftIcon className="h-6 w-6 text-white" />
                  </button>
                </div>
                <div className="flex justify-center gap-6 p-4">
                  <button className="text-white/50 font-medium text-[14px]">
                    {t("professional.reels.category")}
                  </button>
                  <button className={`font-medium text-[14px] ${selectedImage ? 'text-[#F97068]' : 'text-[#fff]'}`}>
                    {t("professional.reels.forMy")}
                  </button>
                  <button className="text-white/50 font-medium text-[14px]">
                    {t("professional.reels.favorite")}
                  </button>
                </div>
              </div>
            </div>

            {selectedImage ? (
              <>
                <img
                  src={selectedImage}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />

                {/* Interaction buttons column */}
                <div className="absolute bottom-20 right-4 flex flex-col items-center gap-4">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-white">
                      <img
                        src={companyImage}
                        alt="Company"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <HeartIcon className="h-7 w-7 text-[#F97068]" />
                    </button>
                    <span className="text-white text-xs">15.4K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <BookmarkIcon className="h-7 w-7 text-white" />
                    </button>
                    <span className="text-white text-xs">2.1K</span>
                  </div>
                  <div className="flex flex-col items-center">
                    <button className="hover:scale-110 transition-transform">
                      <ShareIcon className="h-7 w-7 text-white" />
                    </button>
                    <span className="text-white text-xs">3.8K</span>
                  </div>
                </div>

                {/* Content overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <h4 className="text-white font-semibold">{title}</h4>
                  <p className="text-white text-sm mt-1">{description}</p>
                  <p className="text-white/80 text-sm mt-2 font-medium">
                    {companyData?.category?.name ?? ''}
                  </p>
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                <p className="flex text-center dark:text-dark-cText/60">
                  {t("professional.reels.uploadImage")}
                </p>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-between items-center mt-6 px-0">
            <button
              onClick={() => window.history.back()}
              className="px-6 py-2.5 border border-cBorder dark:border-dark-cBorder 
                  text-cText dark:text-dark-cText 
                  rounded-lg hover:bg-cBackgroundSecondary 
                  dark:hover:bg-dark-cBackgroundSecondary 
                  transition-colors"
            >
              {t("professional.reels.buttonCancel")}
            </button>

            <button
              onClick={handleSubmit}
              className="px-6 py-2.5 bg-bgBrand dark:bg-dark-bgBrand 
                  text-white font-medium rounded-lg 
                  hover:bg-bgBrandHover dark:hover:bg-dark-bgBrandHover 
                  transition-colors"
            >
              {t("professional.reels.buttonAdd")}
            </button>
          </div>
        </div>
      </div>

      <Modal
        isOpen={openModal}
        onClose={closeModal}
        title=""
        hasButtonClose={false}
      >
        <div className="min-h-[200px] max-w-[600px] flex flex-col items-center gap-[20px]">
          <img
            src={emoji}
            alt="success operation"
            className="mx-auto mb-4"
            style={{ width: '100px', height: '100px' }}
          />
          <div className="flex flex-col gap-1">
            <p>{t("professional.reels.hello")}, {companyData?.name ?? ''},</p>
            <p>{t("professional.reels.textModalOne")}</p>
            <p>{t("professional.reels.textModalTwo")}</p>
            <p>{t("professional.reels.textModalThree")}</p>
            <p>{t("professional.reels.textModalFour")}</p>
          </div>
          <button
            onClick={closeModal}
            className="bg-[#83B2A3] text-white px-4 py-2 rounded-lg w-[200px]"
          >
            {t("professional.acceptButton")}
          </button>
        </div>
      </Modal>
    </div>
  );
}
